import type { IsoDateStr } from '@moment/domain-types'
import type { Temporal } from 'temporal-polyfill'

import { parseIsoDate } from './parse-iso-date'

/**
 * The zone a calendar day is resolved in when no other zone is supplied.
 *
 * Moment's data is US market data — a close is stamped at 16:00 ET, an order is
 * placed against an ET trading session — so Eastern is the default. It is a
 * default, not a fact about the data: surfaces that let a user pick a zone pass
 * theirs to the helpers below instead.
 */
export const DEFAULT_TIME_ZONE = 'America/New_York'

/**
 * The instant a calendar day begins in `timeZone` (`2026-06-29` →
 * `2026-06-29T04:00:00Z` in Eastern summer, `05:00Z` in winter).
 *
 * Throws the same way `parseIsoDate` does if `day` carries the brand without
 * being a real ISO date, or if `timeZone` is not a known IANA zone.
 */
export function dayStartInstant(
  day: IsoDateStr,
  timeZone: string = DEFAULT_TIME_ZONE,
): Temporal.Instant {
  return parseIsoDate(day).toZonedDateTime(timeZone).toInstant()
}

/**
 * The instant the *following* calendar day begins — the exclusive upper bound
 * for "on" and "on or before", so the named day is covered whole rather than
 * collapsing to its first second.
 *
 * Derived by adding a day in the calendar rather than 24 hours to the instant,
 * so the daylight-saving days that run 23 and 25 hours land correctly.
 */
export function nextDayStartInstant(
  day: IsoDateStr,
  timeZone: string = DEFAULT_TIME_ZONE,
): Temporal.Instant {
  return parseIsoDate(day)
    .add({ days: 1 })
    .toZonedDateTime(timeZone)
    .toInstant()
}
